import { useMemo, type ReactNode } from "react";
import Warning from "@mui/icons-material/Warning";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";

import { dayjs, NhsNumberSchema } from "@thalamos/common";
import type {
  CapacityState,
  RegisterCoverage,
  RegisterResponse,
  RegisterRow,
} from "@thalamos/aspire-common";
import {
  capacityAssessedAt,
  CapacityStatus,
  capacityStatusOf,
  classifyCapacityExpiry,
  type CapacityExpiryClassification,
} from "@thalamos/aspire-common";

import {
  Banner,
  BannerList,
  Button,
  LoadingSpinner,
} from "~/components/design-system/index.js";
import { routeFns } from "~/routePaths.js";
import { ukLocalFormatDate } from "~/util.js";

export type CareHomeRegisterProps = {
  data?: RegisterResponse;
  isLoading: boolean;
  error?: string;
  /** This organisation's review policy, absent where it has set none. */
  reviewIntervalMonths?: number;
};

const statusColours: Record<
  CapacityStatus,
  { background: string; colour: string }
> = {
  [CapacityStatus.ConsentingWithCapacity]: {
    background: "#E4F2EC",
    colour: "#00674A",
  },
  [CapacityStatus.DolsApplies]: { background: "#E8EFF9", colour: "#1F4785" },
  [CapacityStatus.ValidlyConsenting]: {
    background: "#EEF0F3",
    colour: "#4B5063",
  },
  [CapacityStatus.SafeguardingConcern]: {
    background: "#FBE9E2",
    colour: "#962F0D",
  },
  [CapacityStatus.AwaitingCapacityAssessment]: {
    background: "#FBF2D6",
    colour: "#7A5E00",
  },
  [CapacityStatus.AwaitingConsentAssessment]: {
    background: "#FBF2D6",
    colour: "#7A5E00",
  },
  [CapacityStatus.NoStatus]: { background: "#F2F3F5", colour: "#6C7186" },
};

export function CapacityStatusPill({ state }: { state: CapacityState }) {
  const status = capacityStatusOf(state);
  const { background, colour } = statusColours[status];

  return (
    <Box
      component="span"
      data-testid="capacity-status-pill"
      sx={{
        display: "inline-block",
        backgroundColor: background,
        color: colour,
        borderRadius: "11px",
        padding: "3px 9px",
        fontSize: "0.72rem",
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </Box>
  );
}

/**
 * When this resident's position was last established. A register is scanned to find who
 * nobody has looked at lately, and that question is answered by the date of the last
 * assessment rather than by a projected review date — which is a consequence of policy,
 * not a fact about the resident.
 *
 * The warning still shows: what is overdue is worth seeing at a glance, even though the
 * date it is overdue against lives on the resident page.
 */
function lastAssessedCell(
  state: CapacityState,
  classification: CapacityExpiryClassification,
): ReactNode {
  const assessedAt = capacityAssessedAt(state);
  if (!assessedAt) {
    return "—";
  }

  const needsAttention =
    classification === "expired" ||
    classification === "critical" ||
    classification === "warning";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {needsAttention && (
        <Warning
          sx={{
            fontSize: "1.1rem",
            color: classification === "warning" ? "#E1B205" : "#C74418",
          }}
        />
      )}
      <Box>
        {ukLocalFormatDate(assessedAt)}
        <Box sx={{ fontSize: "0.72rem", color: "#54596B" }}>
          {dayjs(assessedAt).fromNow()}
        </Box>
      </Box>
    </Box>
  );
}

export function CoverageStrip({ coverage }: { coverage: RegisterCoverage }) {
  const cells: { label: string; value: string; colour?: string }[] = [
    { label: "Residents", value: String(coverage.residents) },
    {
      label: "Status current",
      value: String(coverage.statusCurrent),
      colour: "#008758",
    },
    { label: "Due soon", value: String(coverage.dueSoon), colour: "#8A6A00" },
    {
      label: "Out of date",
      value: String(coverage.outOfDate),
      colour: "#C74418",
    },
    // Its own count rather than folded into the current ones: an assessment with no
    // authorisation and no review policy behind it cannot be called up to date.
    { label: "No review date", value: String(coverage.noReviewDate) },
    { label: "Not yet assessed", value: String(coverage.unassessed) },
  ];

  return (
    <Paper
      variant="outlined"
      data-testid="register-coverage"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        marginBottom: "1em",
      }}
    >
      {cells.map((cell) => (
        <Box
          key={cell.label}
          sx={{
            padding: "0.7em 1.2em",
            borderRight: "1px solid #E7E8EA",
            minWidth: "7rem",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.2rem",
              fontWeight: 700,
              lineHeight: 1.2,
              color: cell.colour,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {cell.value}
          </Typography>
          <Typography sx={{ fontSize: "0.65rem", color: "#54596B" }}>
            {cell.label.toUpperCase()}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

export function CareHomeRegisterRow({
  row,
  index,
  reviewIntervalMonths,
}: {
  row: RegisterRow;
  index: number;
  reviewIntervalMonths?: number;
}) {
  const theme = useTheme();
  const state = row.capacityState.state;
  const now = dayjs();
  const classification = classifyCapacityExpiry(
    state,
    now,
    reviewIntervalMonths,
  );

  return (
    <TableRow
      data-testid="register-row"
      sx={{
        backgroundColor:
          index % 2 === 0 ? theme.palette.common.paleBlue : "white",
      }}
    >
      <TableCell>
        <Link
          style={{ textDecoration: "none" }}
          to={routeFns.residentPage(row.rootPatientId)}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontSize: "0.9375rem", color: "blue" }}
          >
            {`${row.patientInfo.name.family.toUpperCase()}, ${row.patientInfo.name.given}`}
          </Typography>
        </Link>
      </TableCell>
      <TableCell>
        {NhsNumberSchema.catch("Unknown").parse(row.patientInfo.nhsNumber)}
      </TableCell>
      <TableCell>
        {row.patientInfo.dateOfBirth
          ? ukLocalFormatDate(row.patientInfo.dateOfBirth)
          : "Unknown"}
      </TableCell>
      <TableCell>
        <CapacityStatusPill state={state} />
      </TableCell>
      <TableCell>
        {state.dols ? (
          <Box>
            {state.dols.status}
            {state.dols.expiryDateTime && (
              <Box sx={{ fontSize: "0.72rem", color: "#54596B" }}>
                {`Review ${ukLocalFormatDate(state.dols.expiryDateTime)}`}
              </Box>
            )}
            {state.urgentAuthorisation && (
              <Box sx={{ fontSize: "0.72rem", color: "#54596B" }}>
                {`+ urgent authorisation to ${ukLocalFormatDate(
                  state.urgentAuthorisation.expiryDateTime,
                )}`}
              </Box>
            )}
          </Box>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell>{lastAssessedCell(state, classification)}</TableCell>
      <TableCell>{row.careHomeName}</TableCell>
      <TableCell align="right">
        {/* Every row opens the resident, where the pathway and history live. Deciding
            what to do belongs on that page, next to the evidence for it. */}
        <Link
          to={routeFns.residentPage(row.rootPatientId)}
          style={{ textDecoration: "none" }}
        >
          <Button label="View" size="small" />
        </Link>
      </TableCell>
    </TableRow>
  );
}

export function CareHomeRegister(props: CareHomeRegisterProps) {
  const headerCellSx = { fontWeight: "bold" };

  const sortedRows = useMemo(() => {
    if (!props.data?.rows) return [];

    // A register is a worklist, so it sorts by what is closest to lapsing rather
    // than by name or by when a row arrived.
    const severity: Record<CapacityExpiryClassification, number> = {
      expired: 0,
      critical: 1,
      warning: 2,
      none: 3,
      normal: 4,
    };
    const now = dayjs();

    return [...props.data.rows].sort((a, b) => {
      const bySeverity =
        severity[
          classifyCapacityExpiry(
            a.capacityState.state,
            now,
            props.reviewIntervalMonths,
          )
        ] -
        severity[
          classifyCapacityExpiry(
            b.capacityState.state,
            now,
            props.reviewIntervalMonths,
          )
        ];
      if (bySeverity !== 0) return bySeverity;

      return a.patientInfo.name.family.localeCompare(b.patientInfo.name.family);
    });
  }, [props.data?.rows, props.reviewIntervalMonths]);

  if (props.isLoading) {
    return (
      <Box
        sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 4 }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  if (props.error) {
    return <Banner bannerType={BannerList.ERROR} title={props.error} />;
  }

  return (
    <Box>
      {props.data && <CoverageStrip coverage={props.data.coverage} />}

      {sortedRows.length === 0 ? (
        <Banner
          bannerType={BannerList.INFO}
          title="No residents on this register yet"
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Resident</TableCell>
                <TableCell sx={headerCellSx}>NHS No.</TableCell>
                <TableCell sx={headerCellSx}>Born</TableCell>
                <TableCell sx={headerCellSx}>Capacity status</TableCell>
                <TableCell sx={headerCellSx}>DoLS tracking</TableCell>
                <TableCell sx={headerCellSx}>Last assessed</TableCell>
                <TableCell sx={headerCellSx}>Care home</TableCell>
                <TableCell sx={headerCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row, index) => (
                <CareHomeRegisterRow
                  reviewIntervalMonths={props.reviewIntervalMonths}
                  key={row.rootPatientId}
                  row={row}
                  index={index}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
